import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { dashboardAPI, salaryAPI, leaveAPI } from '../lib/api';
import { formatCurrency, formatDate, formatMonth, capitalizeFirst } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle, XCircle, Wallet, CalendarDays, Receipt, Calculator, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const REFRESH_INTERVAL = 30000; // 30 seconds

const VerifierDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      setIsRefreshing(true);
      const response = await dashboardAPI.getVerifier();
      setData(response.data);
      setLastRefresh(new Date());
      if (showToast) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to refresh dashboard');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchData();
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const handleSalaryAction = async (salaryId, action) => {
    try {
      await salaryAPI.workflow(salaryId, { action, remarks: `Salary ${action} by verifier` });
      toast.success(`Salary ${action} successful`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    try {
      await leaveAPI.processAction(leaveId, { action });
      toast.success(`Leave ${action} successful`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  // Pendency cards - all clickable
  const pendencyStats = [
    {
      label: 'Pending Leaves',
      value: data?.pending_leave_verification || 0,
      icon: CalendarDays,
      accent: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      color: 'text-blue-600',
      route: '/leave-management',
      testId: 'pending-leaves',
    },
    {
      label: 'Salary Verification',
      value: data?.pending_salary_verification || 0,
      icon: Wallet,
      accent: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      color: 'text-amber-600',
      route: '/salary',
      testId: 'pending-salary',
    },
    {
      label: 'Tax Declarations',
      value: data?.pending_tax_declarations || 0,
      icon: Calculator,
      accent: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      color: 'text-purple-600',
      route: '/tax-declarations',
      testId: 'pending-tax',
    },
    {
      label: 'Pending Claims',
      value: data?.pending_claims || 0,
      icon: Receipt,
      accent: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      color: 'text-orange-600',
      route: '/claims',
      testId: 'pending-claims',
    },
    {
      label: 'Salary Batches',
      value: data?.pending_salary_batches || 0,
      icon: FileText,
      accent: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      color: 'text-indigo-600',
      route: '/salary',
      testId: 'pending-batches',
    },
  ];

  return (
    <div data-testid="verifier-dashboard">
      <Header 
        title="Verifier Dashboard" 
        subtitle="Review and verify pending items"
        action={
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="p-8">
        {/* Pendency Cards - Clickable */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Pending Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {pendencyStats.map((stat) => (
              <div
                key={stat.route || stat.label}
                className={`p-5 rounded-xl border cursor-pointer transition-all duration-200 ${stat.accent} ${stat.value > 0 ? 'ring-2 ring-offset-1' : ''}`}
                onClick={() => navigate(stat.route)}
                data-testid={stat.testId}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`p-3 rounded-lg ${stat.iconBg} mb-3`}>
                    <stat.icon className={`w-6 h-6 ${stat.iconColor}`} strokeWidth={1.5} />
                  </div>
                  <p className={`text-3xl font-heading font-bold ${stat.color}`}>
                    {stat.value}
                  </p>
                  <p className="text-xs font-medium text-slate-600 mt-1">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs for Salary and Leave */}
        <Tabs defaultValue="leave" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="leave" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Leave Verification ({data?.pending_leaves?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="salary" className="flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Salary Verification ({data?.pending_salaries?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Leave Verification */}
          <TabsContent value="leave">
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Leave Applications Pending Verification
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/leave-management')}>
                  View All
                </Button>
              </div>
              
              {data?.pending_leaves?.length === 0 ? (
                <div className="empty-state py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-400" />
                  <h3>All Caught Up!</h3>
                  <p>No leave applications pending verification</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data?.pending_leaves?.map((leave) => (
                    <div
                      key={leave.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      data-testid={`leave-verify-${leave.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {leave.employee_name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{leave.employee_name}</p>
                          <p className="text-sm text-slate-500">
                            {capitalizeFirst(leave.leave_type)} Leave • {leave.days} day(s)
                          </p>
                          <p className="text-xs text-slate-400">
                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </p>
                          {leave.reason && (
                            <p className="text-sm text-slate-600 mt-1">{leave.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-600 hover:bg-emerald-50"
                          onClick={() => handleLeaveAction(leave.id, 'verify')}
                          data-testid={`verify-leave-${leave.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Verify
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleLeaveAction(leave.id, 'reject')}
                          data-testid={`reject-leave-${leave.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Salary Verification */}
          <TabsContent value="salary">
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Salary Records Pending Verification
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/salary')}>
                  View All
                </Button>
              </div>
              
              {data?.pending_salaries?.length === 0 ? (
                <div className="empty-state py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-400" />
                  <h3>All Caught Up!</h3>
                  <p>No salary records pending verification</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Month</th>
                        <th>Gross Salary</th>
                        <th>Deductions</th>
                        <th>Net Pay</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.pending_salaries?.map((salary) => (
                        <tr key={salary.id} data-testid={`salary-verify-${salary.id}`}>
                          <td>
                            <div>
                              <p className="font-medium">{salary.employee_name}</p>
                              <p className="text-xs text-slate-500">{salary.employee_code}</p>
                            </div>
                          </td>
                          <td>{formatMonth(salary.month)}</td>
                          <td>{formatCurrency(salary.gross_salary)}</td>
                          <td className="text-red-600">{formatCurrency(salary.total_deductions)}</td>
                          <td className="font-medium text-emerald-600">{formatCurrency(salary.net_pay)}</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-emerald-600 hover:bg-emerald-50"
                                onClick={() => handleSalaryAction(salary.id, 'verify')}
                                data-testid={`verify-salary-${salary.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Verify
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleSalaryAction(salary.id, 'reject')}
                                data-testid={`reject-salary-${salary.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default VerifierDashboard;
